/**
 * Purpose: Centralize all logic for message size and limit handling
 *
 * How it works:
 * 1. Defines consistent limits for message content:
 *    - User-facing character limit (plain text characters)
 *    - Backend encrypted size limit (AES-GCM ciphertext encoded as base64 or hex chars)
 * 2. Provides pure utility functions to:
 *    - Predict encrypted payload size from HTML content
 *    - Compute limit state (usage, remaining characters, warnings)
 * 3. Designed to be UI-agnostic so it can be reused by different inputs
 *
 * Key features:
 * - Single source of truth for all message limit numbers
 * - Accurate AES-GCM + encoding overhead modeling (base64 or hex)
 * - Dynamic remaining-character estimation based on actual content
 * - "Close to limit" warning based on whichever limit is tighter
 */

import { FLAG_USE_BASE64_ENCODING } from "./crypto/encodingConfig";

export const MESSAGE_CHAR_LIMIT = 10_000;
// Backend limit on the encoded encrypted content field. The unit is *encoded
// characters* (base64 or hex chars, depending on FLAG_USE_BASE64_ENCODING),
// not raw bytes — kept under this name for backwards compatibility with
// existing callers/tests. See getEncryptedEncodedLength for the matching
// computation.
export const MAX_ENCRYPTED_BYTES = 32 * 1024;

const AES_GCM_NONCE_BYTES = 12;
const AES_GCM_TAG_BYTES = 16;

// Fixed overhead in raw bytes added by AES-GCM (nonce + auth tag)
const AES_GCM_OVERHEAD_BYTES = AES_GCM_NONCE_BYTES + AES_GCM_TAG_BYTES;

// Maximum plaintext bytes that will still encrypt into <= MAX_ENCRYPTED_BYTES encoded chars.
// Base64: ceil((plaintextBytes + overhead) / 3) * 4 <= MAX_ENCRYPTED_BYTES
// Hex:    (plaintextBytes + overhead) * 2 + 2 <= MAX_ENCRYPTED_BYTES ("0x" prefix)
const MAX_PLAINTEXT_BYTES = FLAG_USE_BASE64_ENCODING
  ? Math.floor(MAX_ENCRYPTED_BYTES / 4) * 3 - AES_GCM_OVERHEAD_BYTES
  : Math.floor((MAX_ENCRYPTED_BYTES - 2) / 2) - AES_GCM_OVERHEAD_BYTES;

export const CLOSE_TO_LIMIT_THRESHOLD = 0.8; // 80% used => 20% remaining

export type MessageLimitInput = {
  /** Plain text version of the message (e.g. editor.getText()) */
  plainText: string;
  /** HTML version of the message that will actually be encrypted */
  html: string;
};

export type MessageLimitState = {
  /** Current plain text character count */
  charCount: number;
  /** Configured maximum allowed characters */
  charLimit: number;
  /**
   * Approximate number of additional characters the user can type
   * before hitting either the character or encrypted size limit.
   *
   * This is derived from the current bytes-per-character ratio and the
   * maximum plaintext bytes allowed by the encrypted size limit.
   */
  approxRemainingChars: number;
  /** True when the plain text character count exceeds the configured limit */
  isOverCharLimit: boolean;
  /**
   * True when the predicted encrypted payload size would exceed the
   * backend limit for the content field.
   */
  isOverEncryptedLimit: boolean;
  /**
   * True when we are close to either the character limit or the
   * encrypted size limit (>= 80% usage, i.e. <= 20% remaining).
   */
  showCloseToLimitWarning: boolean;
  /** Predicted encrypted payload size in encoded characters (base64 or hex) */
  encryptedSizeBytes: number;
  /** Configured maximum encrypted payload size in encoded characters */
  maxEncryptedBytes: number;
};

// Shared encoder instance to avoid repeated allocations
const textEncoder = new TextEncoder();

/**
 * Returns the UTF-8 byte length of a string.
 * Uses a shared TextEncoder for efficiency.
 */
export function getUtf8ByteLength(str: string): number {
  return textEncoder.encode(str).length;
}

/**
 * Predicts the encoded encrypted length for a given HTML message.
 *
 * How it works:
 * 1. Encodes the HTML string as UTF-8 bytes
 * 2. Adds AES-GCM overhead:
 *    - 12-byte IV (nonce)
 *    - 16-byte authentication tag
 * 3. Converts to the encoded representation selected by
 *    `FLAG_USE_BASE64_ENCODING`:
 *    - base64: 4 chars per 3 bytes (with padding)
 *    - hex: 2 chars per byte plus the `0x` prefix
 *
 * This mirrors the behavior of `encryptString()` in the crypto module
 * (which conditionally returns base64 or hex) so we can validate messages
 * before sending under either encoding.
 */
export function getEncryptedEncodedLength(html: string): number {
  const plaintextBytes = getUtf8ByteLength(html);
  const encryptedBytes = plaintextBytes + AES_GCM_OVERHEAD_BYTES;

  if (FLAG_USE_BASE64_ENCODING) {
    return Math.ceil(encryptedBytes / 3) * 4;
  }
  // Hex: each byte → 2 hex chars, plus "0x" prefix
  return encryptedBytes * 2 + 2;
}

/**
 * Computes the current message limit state for the given content.
 *
 * How it works:
 * 1. Counts plain text characters for the user-facing 10k limit
 * 2. Predicts encrypted payload size using getEncryptedEncodedLength
 * 3. Determines which constraint is tighter at the moment
 * 4. Estimates how many additional characters can be added by:
 *    - Calculating current bytes-per-character from the actual content
 *    - Converting remaining plaintext bytes into approximate characters
 *    - Respecting both the character and backend size limits
 * 5. Sets a "close to limit" flag when there is <= 20% headroom
 */
export function computeMessageLimitState(
  input: MessageLimitInput,
): MessageLimitState {
  const { plainText, html } = input;

  const charCount = plainText.length;
  const charLimit = MESSAGE_CHAR_LIMIT;

  const encryptedSizeBytes = getEncryptedEncodedLength(html);

  const charUsageRatio = charLimit > 0 ? charCount / charLimit : 0;
  const encryptedUsageRatio =
    MAX_ENCRYPTED_BYTES > 0 ? encryptedSizeBytes / MAX_ENCRYPTED_BYTES : 0;

  const isOverCharLimit = charCount > charLimit;
  const isOverEncryptedLimit = encryptedSizeBytes > MAX_ENCRYPTED_BYTES;

  const overallUsageRatio = Math.max(charUsageRatio, encryptedUsageRatio);

  const showCloseToLimitWarning =
    !isOverCharLimit &&
    !isOverEncryptedLimit &&
    overallUsageRatio >= CLOSE_TO_LIMIT_THRESHOLD;

  // Estimate how many more characters we can safely accept

  // 1. Compute current plaintext byte length (HTML) for encrypted size math
  const plaintextBytes = getUtf8ByteLength(html);

  // Use plain text byte length for bytes/char estimation to avoid inflation from HTML markup
  const plaintextTextBytes = getUtf8ByteLength(plainText);

  // Fallback to a conservative 4 bytes/char when we have no text yet
  const bytesPerChar = charCount > 0 ? plaintextTextBytes / charCount : 4;

  // 2. How many plaintext bytes remain before we exceed the encrypted limit
  const remainingPlaintextBytes = Math.max(
    MAX_PLAINTEXT_BYTES - plaintextBytes,
    0,
  );

  const remainingByEncryptedLimit = Math.floor(
    remainingPlaintextBytes / Math.max(bytesPerChar, 1),
  );

  // 3. Remaining characters by the hard character limit
  const remainingByCharLimit = Math.max(charLimit - charCount, 0);

  // 4. Final remaining characters is the tightest of the two constraints
  const approxRemainingChars = Math.max(
    0,
    Math.min(remainingByEncryptedLimit, remainingByCharLimit),
  );

  return {
    charCount,
    charLimit,
    approxRemainingChars,
    isOverCharLimit,
    isOverEncryptedLimit,
    showCloseToLimitWarning,
    encryptedSizeBytes,
    maxEncryptedBytes: MAX_ENCRYPTED_BYTES,
  };
}

export const messageLimitConfig = {
  MESSAGE_CHAR_LIMIT,
  MAX_ENCRYPTED_BYTES,
  CLOSE_TO_LIMIT_THRESHOLD,
};
